import { z } from 'zod'
import { isValidPhone } from '~/utils/phone'

/**
 * zod building blocks shared by the member and SubKrewe forms. Messages are
 * written for the person filling the form in.
 */

/** A required text field, trimmed. */
export const requiredText = (message: string, max = 255) =>
  z.string().trim().min(1, message).max(max, `Keep it under ${max} characters`)

/** An optional text field, trimmed. */
export const optionalText = (max = 255) =>
  z.string().trim().max(max, `Keep it under ${max} characters`)

const isEmail = (value: string) => z.email().safeParse(value).success

export const emailField = z.string().trim()
  .min(1, 'Email is required')
  .max(255, 'Keep it under 255 characters')
  .refine(isEmail, 'Enter a valid email address')

export const optionalEmailField = z.string().trim()
  .max(255, 'Keep it under 255 characters')
  .refine(value => !value || isEmail(value), 'Enter a valid email address')

/** A phone in stored form ("+15045551234"); empty is allowed. */
export const optionalPhoneField = z.string()
  .refine(value => !value || isValidPhone(value), 'Enter the full phone number')

/** A phone in stored form, required. */
export const phoneField = z.string()
  .min(1, 'Phone is required')
  .refine(value => isValidPhone(value), 'Enter the full phone number')

/** A date as the date input gives it: "YYYY-MM-DD". */
export const requiredDate = (message: string) =>
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/, message)

/** Postal address as members store it; a US address needs a state. */
export const addressSchema = z.object({
  address: requiredText('Street is required'),
  city: requiredText('City is required'),
  country: z.string().min(1, 'Country is required'),
  state: z.string().optional(),
  zip: requiredText('Zip is required', 20)
    .regex(/^[A-Za-z0-9 -]+$/, 'Letters, numbers, spaces and dashes only'),
}).superRefine((value, ctx) => {
  if (value.country === 'US' && !value.state) {
    ctx.addIssue({ code: 'custom', path: ['state'], message: 'State is required' })
  }
})

export type AddressValues = z.input<typeof addressSchema>

/** Address values for a form, with the defaults the old forms used. */
export function addressValues(address: Partial<AddressValues> | null | undefined): AddressValues {
  return {
    address: address?.address ?? '',
    city: address?.city ?? '',
    country: address?.country || 'US',
    state: address?.state ?? (address?.country && address.country !== 'US' ? '' : 'LA'),
    zip: address?.zip ?? '',
  }
}
